import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { Box } from "@mui/material";
import type { ReactNode } from "react";
import { FONT_SIZE, INK, MOTION, NEUTRAL, SURFACE } from "../../../theme/tokens";
import { TOUCH_TARGET_SX } from "../touchTarget";

export type SystemChoiceItem = {
	value: string;
	label: string;
	/**
	 * Rich row body rendered INSTEAD of the plain `label` — for an option that needs more than one line
	 * of text (a person chip plus its metadata, say). `label` stays required and becomes the row's
	 * accessible name, so a composed row is never left announcing whatever its markup happens to contain.
	 * Must be phrasing content (`component="span"`), since the row itself is a `<button>`.
	 */
	content?: ReactNode;
	dataTestId?: string;
};

type Props = {
	items: SystemChoiceItem[];
	onSelect: (value: string) => void;
	/** Disables every row without changing the layout (e.g. while a pick is in flight / the picker expired). */
	disabled?: boolean;
	/** Accessible name for the option group. */
	ariaLabel?: string;
	dataTestId?: string;
};

/**
 * A single-select vertical list of full-width option rows whose labels WRAP — the readable answer for
 * long, end-distinguished choices (e.g. shift labels differing only in their trailing time) that a chip
 * row would overflow and a dropdown would truncate. Selecting a row IS the answer (no confirm step),
 * mirroring `SystemChips`. Rows reuse the same hover/active/focus affordances as the Annie pickup table.
 */
export const SystemChoiceList = ({ items, onSelect, disabled = false, ariaLabel, dataTestId }: Props) => {
	return (
		<Box role="group" aria-label={ariaLabel} data-testid={dataTestId} sx={{ display: "flex", flexDirection: "column", gap: 1, width: "100%" }}>
			{items.map((item, index) => {
				return (
					<Box
						component="button"
						type="button"
						// biome-ignore lint/suspicious/noArrayIndexKey: options are positional and have no stable id of their own.
						key={index}
						disabled={disabled}
						data-testid={item.dataTestId}
						aria-label={item.label}
						onClick={() => onSelect(item.value)}
						sx={{
							...TOUCH_TARGET_SX,
							display: "flex",
							alignItems: "center",
							gap: 1,
							width: "100%",
							boxSizing: "border-box",
							px: 1.5,
							py: 1.25,
							textAlign: "start",
							border: `1px solid ${NEUTRAL[200]}`,
							borderRadius: 2,
							backgroundColor: SURFACE,
							fontFamily: "inherit",
							fontSize: FONT_SIZE.SECONDARY,
							fontWeight: 600,
							lineHeight: 1.4,
							color: INK.PRIMARY,
							// The long-label failure this list exists to solve: wrap across lines, break unbreakable tokens.
							overflowWrap: "anywhere",
							"&:disabled": { opacity: 0.6, cursor: "default" },
							"&:not(:disabled)": {
								cursor: "pointer",
								transition: `background-color ${MOTION.DURATION.HOVER}ms ease, border-color ${MOTION.DURATION.HOVER}ms ease`,
								"@media (hover: hover) and (pointer: fine)": {
									"&:hover": { backgroundColor: NEUTRAL[50], borderColor: NEUTRAL[400] },
								},
								"&:active": { backgroundColor: NEUTRAL[150] },
								"&:focus-visible": { outline: `2px solid ${NEUTRAL[400]}`, outlineOffset: "-2px" },
								"@media (prefers-reduced-motion: reduce)": { transition: "none" },
							},
						}}
					>
						<Box component="span" sx={{ flex: 1, minWidth: 0 }}>
							{item.content ?? item.label}
						</Box>
						<ChevronRightIcon sx={{ fontSize: 18, color: INK.MUTED, flexShrink: 0 }} />
					</Box>
				);
			})}
		</Box>
	);
};
